import { useState, useEffect } from "react";
import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Home, PlusCircle, User, LogOut, Bell, Users, Timer, LayoutDashboard, Trophy, TrendingUp, Menu, Calculator, Sparkles, Shield, Award } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/hooks/useTheme";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { usePremiumStatus } from "@/hooks/usePremiumStatus";
import { PremiumCelebration } from "@/components/PremiumCelebration";
import { AnimatePresence, motion } from "framer-motion";
import fitlinkLogo from "@/assets/fitlink-logo-main.png";
import { useLanguage } from "@/hooks/useLanguage";
import { useAnalytics } from "@/hooks/useAnalytics";

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { theme } = useTheme();
  const { isAdmin } = useAdminStatus();
  const { justBecamePremium, dismissCelebration } = usePremiumStatus();
  const { t } = useLanguage();
  const { trackClick, trackFeature } = useAnalytics();
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [desktopSheetOpen, setDesktopSheetOpen] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Set up auth state listener FIRST (critical for session persistence)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      // Only synchronous state updates here
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (event === "SIGNED_OUT") {
        navigate("/auth");
      } else if (currentSession?.user) {
        // Defer Supabase calls with setTimeout to prevent deadlocks
        setTimeout(() => {
          fetchProfile(currentSession.user.id);
        }, 0);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (!existingSession) {
        navigate("/auth");
      } else {
        setSession(existingSession);
        setUser(existingSession.user);
        fetchProfile(existingSession.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    if (data) setProfile(data);
  };

  const fetchUnreadCount = async () => {
    if (!user?.id) return;
    
    const { count } = await supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("read", false);
    
    setUnreadCount(count || 0);
  };

  useEffect(() => {
    if (user?.id) {
      fetchUnreadCount();

      // Subscribe to notification changes
      const channel = supabase
        .channel("notifications-changes")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            fetchUnreadCount();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user?.id]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: t("toast.signedOut"),
    });
  };

  if (!user) return null;

  return (
    <div className="min-h-screen min-h-dvh bg-background transition-colors duration-300 flex flex-col flex-1">
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-card/95 backdrop-blur-lg shadow-soft">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img 
                src={fitlinkLogo} 
                alt="FITLINK" 
                className="w-10 h-10 rounded-lg" 
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-foreground leading-none">
                  FITLINK
                </span>
                <span className="text-[10px] text-muted-foreground leading-none mt-0.5">
                  Stay fit, stay linked.
                </span>
              </div>
            </div>

            {/* Desktop Navigation - Primary */}
            <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground font-semibold shadow-md"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                <Home className="w-4 h-4" />
                <span>{t("nav.feed")}</span>
              </NavLink>

              <NavLink
                to="/friends"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground font-semibold shadow-md"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                <Users className="w-4 h-4" />
                <span>{t("nav.friends")}</span>
              </NavLink>

              <NavLink
                to="/analytics"
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground font-semibold shadow-md"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`
                }
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>{t("nav.analytics")}</span>
              </NavLink>

              <div className="w-px h-8 bg-border mx-2" />

              <Sheet open={desktopSheetOpen} onOpenChange={setDesktopSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Menu className="w-4 h-4 mr-2" />
                    {t("nav.more")}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <SheetHeader>
                    <SheetTitle>{t("nav.more")}</SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col gap-2 mt-6">
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        className="justify-start h-12 border-primary/50"
                        onClick={() => {
                          navigate("/admin");
                          setDesktopSheetOpen(false);
                        }}
                      >
                        <Shield className="w-4 h-4 mr-3 text-primary" />
                        Admin Dashboard
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      className="justify-start h-12"
                      onClick={() => {
                        navigate("/achievements");
                        setDesktopSheetOpen(false);
                      }}
                    >
                      <Award className="w-4 h-4 mr-3" />
                      {t("nav.achievements")}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start h-12"
                      onClick={() => {
                        navigate("/leaderboard");
                        setDesktopSheetOpen(false);
                      }}
                    >
                      <Trophy className="w-4 h-4 mr-3" />
                      {t("nav.leaderboard")}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start h-12"
                      onClick={() => {
                        navigate("/strength-calculator");
                        setDesktopSheetOpen(false);
                      }}
                    >
                      <Calculator className="w-4 h-4 mr-3" />
                      1RM Calculator
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start h-12 border-primary/30 bg-primary/5"
                      onClick={() => {
                        navigate("/body-assessment");
                        setDesktopSheetOpen(false);
                      }}
                    >
                      <Sparkles className="w-4 h-4 mr-3 text-primary" />
                      AI Physique Coach
                    </Button>
                    <Button 
                      variant="default" 
                      className="justify-start h-12 bg-gradient-to-r from-yellow-400 to-amber-500 text-black hover:from-yellow-500 hover:to-amber-600"
                      onClick={() => {
                        navigate("/premium");
                        setDesktopSheetOpen(false);
                      }}
                    >
                      <Sparkles className="w-4 h-4 mr-3" />
                      {t("nav.premium")}
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop Navigation - Actions */}
            <div className="hidden md:flex items-center gap-2">
              <Button 
                variant="default" 
                size="sm" 
                onClick={() => {
                  trackClick('new_workout_button', 'New Workout');
                  navigate("/workout");
                }}
                className="shadow-glow"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {t("nav.newWorkout")}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => navigate("/notifications")} className="relative">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 shadow-lg animate-in zoom-in-50">
                    {unreadCount > 100 ? "100+" : unreadCount}
                  </span>
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-2">
                <LogOut className="w-4 h-4" />
                {t("common.signOut")}
              </Button>
              <Avatar className="w-9 h-9 border-2 border-primary cursor-pointer ring-2 ring-primary/20" onClick={() => navigate("/profile")}>
                <AvatarImage src={profile?.avatar_url} />
                <AvatarFallback className="bg-gradient-primary text-primary-foreground font-bold">
                  {profile?.username?.charAt(0).toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Mobile - Just notifications and profile */}
            <div className="flex md:hidden items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => navigate("/notifications")} className="relative">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 shadow-lg animate-in zoom-in-50">
                    {unreadCount > 100 ? "100+" : unreadCount}
                  </span>
                )}
              </Button>
              <Avatar className="w-9 h-9 border-2 border-primary cursor-pointer" onClick={() => navigate("/profile")}>
                <AvatarImage src={profile?.avatar_url} />
                <AvatarFallback className="bg-gradient-primary text-primary-foreground font-bold">
                  {profile?.username?.charAt(0).toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6 pt-24 touch-auto flex-1">
        <Outlet />
      </main>

      {/* Bottom Navigation - Mobile */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur-lg shadow-strong md:hidden pb-safe" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}>
        <div className="container mx-auto px-2">
          <div className="grid grid-cols-5 h-20">
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 transition-all ${
                  isActive ? "text-primary scale-105" : "text-muted-foreground"
                }`
              }
            >
              <Home className="w-7 h-7" />
              <span className="text-[10px] font-medium">{t("nav.feed")}</span>
            </NavLink>

            <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
              <SheetTrigger asChild>
                <button className="flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition-all touch-auto cursor-pointer">
                  <Menu className="w-7 h-7" />
                  <span className="text-[10px] font-medium">{t("nav.more")}</span>
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto">
                <SheetHeader>
                  <SheetTitle>{t("nav.more")}</SheetTitle>
                </SheetHeader>
                <div className="grid grid-cols-2 gap-3 mt-6 mb-4">
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      className="h-20 flex flex-col gap-2 border-primary/50"
                      onClick={() => {
                        navigate("/admin");
                        setMobileSheetOpen(false);
                      }}
                    >
                      <Shield className="w-6 h-6 text-primary" />
                      <span className="text-sm font-semibold">Admin</span>
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col gap-2"
                    onClick={() => {
                      navigate("/achievements");
                      setMobileSheetOpen(false);
                    }}
                  >
                    <Award className="w-6 h-6" />
                    <span className="text-sm font-semibold">{t("nav.achievements")}</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col gap-2"
                    onClick={() => {
                      navigate("/leaderboard");
                      setMobileSheetOpen(false);
                    }}
                  >
                    <Trophy className="w-6 h-6" />
                    <span className="text-sm font-semibold">{t("nav.leaderboard")}</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col gap-2"
                    onClick={() => {
                      navigate("/strength-calculator");
                      setMobileSheetOpen(false);
                    }}
                  >
                    <Calculator className="w-6 h-6" />
                    <span className="text-sm font-semibold">1RM Calculator</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col gap-2"
                    onClick={() => {
                      navigate("/friends");
                      setMobileSheetOpen(false);
                    }}
                  >
                    <Users className="w-6 h-6" />
                    <span className="text-sm font-semibold">{t("nav.friends")}</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col gap-2 border-primary/30 bg-primary/5"
                    onClick={() => {
                      navigate("/body-assessment");
                      setMobileSheetOpen(false);
                    }}
                  >
                    <Sparkles className="w-6 h-6 text-primary" />
                    <span className="text-sm font-semibold">AI Coach</span>
                  </Button>
                  <Button 
                    variant="default" 
                    className="h-20 flex flex-col gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black hover:from-yellow-500 hover:to-amber-600"
                    onClick={() => {
                      navigate("/premium");
                      setMobileSheetOpen(false);
                    }}
                  >
                    <Sparkles className="w-6 h-6" />
                    <span className="text-sm font-semibold">{t("nav.premium")}</span>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <NavLink
              to="/workout"
              className="flex flex-col items-center justify-center -mt-6"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center shadow-glow border-4 border-background">
                <PlusCircle className="w-8 h-8 text-primary-foreground" />
              </div>
            </NavLink>

            <NavLink
              to="/analytics"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 transition-all ${
                  isActive ? "text-primary scale-105" : "text-muted-foreground"
                }`
              }
            >
              <TrendingUp className="w-7 h-7" />
              <span className="text-[10px] font-medium">Stats</span>
            </NavLink>

            <NavLink
              to="/profile"
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 transition-all ${
                  isActive ? "text-primary scale-105" : "text-muted-foreground"
                }`
              }
            >
              <User className="w-7 h-7" />
              <span className="text-[10px] font-medium">Profile</span>
            </NavLink>
          </div>
        </div>
      </nav>

      {/* Bottom padding for mobile nav */}
      <div className="h-20 md:hidden pb-safe" />
      
      {/* Premium Celebration Modal */}
      <PremiumCelebration open={justBecamePremium} onOpenChange={dismissCelebration} />
    </div>
  );
};

export default Layout;
